/**
 * Formats a deal value in the agency's currency, without cents, e.g. $1,250,000
 * (AUD/USD/NZD/CAD/SGD), €1,250,000 (EUR), £1,250,000 (GBP). The currency sign and
 * grouping separators come from Intl; defaults to AUD for backward compatibility.
 * A null/undefined value renders as "—" (deal values are nullable) rather than "$0".
 */
export function formatDealValue(value: number | null | undefined, currency = 'AUD'): string {
  if (value == null) return '—'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * A money figure shortened for a headline or an axis — "$53.4M", "$850K". Use the full
 * {@link formatDealValue} wherever the exact amount matters (tables, tooltips).
 */
export function formatCompactValue(value: number | null | undefined, currency = 'AUD'): string {
  if (value == null) return '—'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

/**
 * Parses an ISO date string. Date-only values ('YYYY-MM-DD') are parsed as
 * LOCAL dates — new Date('YYYY-MM-DD') would give UTC midnight, which shifts
 * the calendar day in timezones west of UTC. Full instants (with 'T') parse as usual.
 */
function parseIsoDate(iso: string): Date {
  if (!iso.includes('T')) {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  return new Date(iso)
}

/** Formats an ISO date as a short date, e.g. "9 Jul 2026". */
export function formatShortDate(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * The local calendar date (YYYY-MM-DD) that an ISO instant falls on — the same day
 * `formatShortDate` shows. Use this to compare an instant against a date-input value
 * (also local YYYY-MM-DD); slicing the raw UTC instant would drift a day in AU.
 */
export function localDate(iso: string): string {
  return localIso(parseIsoDate(iso))
}

/** True when the date is before the start of today (next contact is overdue). */
export function isOverdue(iso: string): boolean {
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  return parseIsoDate(iso) < startOfToday
}

/** Legacy date-only tasks were stored at exactly 00:00:00 UTC (new ones use end-of-day local time). */
const LEGACY_MIDNIGHT_UTC = /T00:00(:00)?(\.0+)?Z$/

/**
 * True when a task's due instant has already passed — time-aware (a task due today at 09:00 becomes
 * overdue at 09:01), unlike the date-based {@link isOverdue} used for the next-contact date. Tasks
 * created with only a date are stored at end-of-day, so a date-only task isn't overdue until the day
 * is over; legacy date-only tasks (00:00 UTC) are treated the same rather than flipping mid-day.
 */
export function isTaskOverdue(dueAt: string): boolean {
  const d = new Date(dueAt)
  if (LEGACY_MIDNIGHT_UTC.test(dueAt)) d.setHours(23, 59, 59, 999)
  return d.getTime() < Date.now()
}

/**
 * A task's due date, with the time appended only when it's a meaningful time-of-day — a date-only
 * task (stored at end-of-day 23:59) or a legacy midnight task (00:00 UTC) shows the date alone.
 */
export function formatTaskDue(iso: string): string {
  const d = parseIsoDate(iso)
  const date = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
  if (LEGACY_MIDNIGHT_UTC.test(iso) || (d.getHours() === 23 && d.getMinutes() === 59)) return date
  return `${date}, ${d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}`
}

/** Tasks soonest-due first, the undated ones last — the order they need doing in. */
export function byDueDate(a: { dueAt: string | null }, b: { dueAt: string | null }): number {
  if (!a.dueAt || !b.dueAt) return a.dueAt ? -1 : b.dueAt ? 1 : 0
  return parseIsoDate(a.dueAt).getTime() - parseIsoDate(b.dueAt).getTime()
}

/** Whole days elapsed since the given instant, clamped at 0 (e.g. time in the current stage). */
/**
 * Whole calendar days since the given date (local): 0 = today, 1 = yesterday, … never negative.
 * Calendar-day based (like {@link daysUntil}, sharing its local-midnight comparison) so "Time in
 * stage: 0 days / Entered today" means the deal entered the stage on today's local date — not merely
 * within the last 24 hours (which would call a deal that entered late yesterday "today" this morning).
 */
export function daysSince(iso: string): number {
  return Math.max(0, -daysUntil(iso))
}

/**
 * Signed whole calendar days from today to the given date (local): +N future, -N past, 0 today.
 * Compares calendar days (not raw instants) so a same-day time still reads as 0, not "yesterday".
 */
export function daysUntil(iso: string): number {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  const target = parseIsoDate(iso)
  target.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - start.getTime()) / 86_400_000)
}

/** Human relative day, e.g. "today", "tomorrow", "in 8 days", "yesterday", "3 days ago". */
export function relativeDay(iso: string): string {
  const d = daysUntil(iso)
  if (d === 0) return 'today'
  if (d === 1) return 'tomorrow'
  if (d === -1) return 'yesterday'
  return d > 0 ? `in ${d} days` : `${-d} days ago`
}

/** A local Date as YYYY-MM-DD (date inputs compare lexically, so no timezone drift). */
function localIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Today's date as YYYY-MM-DD in the user's local timezone. */
export function todayIso(): string {
  return localIso(new Date())
}

/** The date `days` from today as YYYY-MM-DD in the user's local timezone. */
export function isoInDays(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return localIso(d)
}
